import { Store } from '../../core/createStore';
import Order from '../../entity/order';
import OrderRepository from '../../service/orderRepository';
import ProductRepository from '../../service/productRepository';
import ProductCategoryRepository, {
  SearchParamType as ProductCategorySearchParamType,
} from '../../service/productCategoryRepository';
import {
  dispose,
  setProductCategories,
  setOrderProducts,
  nextProductPage,
  SearchProductType,
  OrderProductType,
  setOrder,
  startFetch,
  finishFetch,
} from "../../state/contentSelect";
import PageInfo from "../../entity/pageInfo";
import OrderProduct from "../../entity/orderProduct";

class ContentSelect {
  private orderRepository: OrderRepository;
  private productRepository: ProductRepository;
  private productCategoryRepository: ProductCategoryRepository;
  private store: Store;

  constructor({
    orderRepository,
    productRepository,
    productCategoryRepository,
    store,
  }: {
    orderRepository: OrderRepository;
    productRepository: ProductRepository;
    productCategoryRepository: ProductCategoryRepository;
    store: Store;
  }) {
    this.orderRepository = orderRepository;
    this.productRepository = productRepository;
    this.productCategoryRepository = productCategoryRepository;
    this.store = store;

    this.getProducts = this.getProducts.bind(this);
  }

  public async initialize(): Promise<void> {
    const session = this.store.getState().currentSession;
    const currentStore = this.store.getState().currentStore;
    const { productSearchParams } = this.store.getState().contentSelect;

    try {
      if (!session || !currentStore) {
        throw new Error('It is cannot have reach here.');
      }
      this.store.dispatch(startFetch());
      const productCategorySearchParams = {} as ProductCategorySearchParamType;

      const res = await this.orderRepository.getOrderWithProducts(session);
      const orderProducts = await new Map(
        await Promise.all(
          await res.orderProducts.map(async orderProduct => {
            return [orderProduct.productId as string, orderProduct] as [string, OrderProduct];
          }),
        ),
      );
      const newOrder = await this.updateOrderPrice(res.order, orderProducts);
      const productCategories =await this.productCategoryRepository.getProductCategories(session, currentStore.id, productCategorySearchParams);

      const resProducts = await this.getProducts(currentStore.id, productSearchParams);

      this.store.dispatch(setProductCategories(productCategories));
      this.store.dispatch(finishFetch({
        productCategories,
        order: newOrder,
        products: resProducts.records,
        productPageInfo: resProducts.pageInfo,
        orderProducts,
      }));
    } catch (e) {
      this.store.dispatch(finishFetch(null));
      throw e;
    }
  }

  public async searchProducts(
    currentStoreId: string,
    searchParams: SearchProductType,
  ) {
    const {
      productSearchParams,
      productPageInfo,
      orderProducts,
    } = this.store.getState().contentSelect;
    const searchParam = {
      ...productSearchParams,
      ...searchParams,
    };

    const res = await this.getProducts(currentStoreId, searchParam, productPageInfo);

    this.store.dispatch(nextProductPage({
      orderProducts,
      pageInfo: res.pageInfo,
      products: res.records,
      searchParam,
    }));
  }

  public async productNextPage(): Promise<PageInfo> {
    const session = this.store.getState().currentSession;
    const currentStore = this.store.getState().currentStore;
    const {
      productSearchParams,
      productPageInfo,
      orderProducts,
      products,
    } = this.store.getState().contentSelect;

    if (!session || !currentStore) {
      throw new Error('It is cannot have reach here.');
    }

    const res = await this.productRepository.getProducts(
      session,
      currentStore.id,
      productPageInfo.nextPageConditions(productSearchParams.order),
      productSearchParams.name,
      productSearchParams.searchInput,
    );

    const newProducts = products.concat(res.records);

    this.store.dispatch(nextProductPage({
      orderProducts,
      products: newProducts,
      pageInfo: res.pageInfo,
      searchParam: productSearchParams,
    }));

    return res.pageInfo;
  }

  public async updateEditOrderProductField(orderProduct: OrderProduct, count: number): Promise<void> {
    const {
      orderProducts,
      order,
    } = this.store.getState().contentSelect;
    const newOrderProduct = new OrderProduct({ ...orderProduct, count });
    orderProducts.set(orderProduct.productId, newOrderProduct);
    const newOrderProducts = new Map(orderProducts.entries());
    const newOrder = await this.updateOrderPrice(order || new Order({}), newOrderProducts);
    this.store.dispatch(setOrder(newOrder));
    this.store.dispatch(setOrderProducts(newOrderProducts));
  }

  public async updateOrderPrice(order: Order, orderProducts: OrderProductType) {
    let totalProductCount = 0;
    let totalProductPrice = 0;
    let totalTaxPrice = 0;

    for await (const orderProduct of orderProducts.values()) {
      totalProductCount += orderProduct.count;
      totalProductPrice += orderProduct.price * orderProduct.count;
      totalTaxPrice += orderProduct.taxPrice * orderProduct.count;
    }
    return new Order({
      ...order,
      totalTaxPrice,
      totalProductPrice,
      totalProductCount,
      totalPrice: totalProductPrice + totalTaxPrice,
    });
  }

  public async save() {
    const session = this.store.getState().currentSession;
    const currentStore = this.store.getState().currentStore;
    const { order, orderProducts } = this.store.getState().contentSelect;

    if (!session || !order || !currentStore) {
      throw new Error('It is cannot have reach here.');
    }

    try {
      const newOrder = new Order({ ...order } as Order);

      await this.orderRepository.saveOrder(session, {
        order: newOrder as Order,
        orderProducts: Array.from(orderProducts.values()),
      });
    } catch (err) {
      throw err;
    }
  }

  public dispose(): void {
    this.store.dispatch(dispose());
  }

  private async getProducts(
    currentStoreId: string,
    searchParams: SearchProductType,
    pageInfo?: PageInfo,
  ) {
    const session = this.store.getState().currentSession;
    const { productPageInfo } = this.store.getState().contentSelect;
    const currentPageInfo = pageInfo || productPageInfo;

    if (!session) {
      throw new Error('It is cannot have reach here.');
    }

    return await this.productRepository.getProducts(
      session,
      currentStoreId,
      currentPageInfo.firstPageConditions(searchParams.order),
      searchParams.name,
      searchParams.searchInput,
    );
  }
}

export default ContentSelect;
